import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, CheckCircle, Calendar, MapPin, ChevronRight, Filter, Search } from 'lucide-react';
import api from '../../api/apiClient';

interface Job {
  id: number;
  propertyAddress: string;
  category: string;
  description: string;
  urgency: string;
  status: string;
  scheduledDate?: string;
  quoteAmount?: number;
  createdAt: string;
}

export default function ContractorJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/contractor/dashboard');
      if (response) {
        setJobs(response.upcomingJobs || []);
      }
    } catch (err: any) {
      console.error('Failed to load jobs:', err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ASSIGNED': return 'bg-blue-100 text-blue-800';
      case 'SCHEDULED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'QUOTE_PENDING': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toUpperCase()) {
      case 'EMERGENCY': return 'text-red-600';
      case 'HIGH': return 'text-orange-600';
      case 'MEDIUM': return 'text-yellow-600';
      case 'LOW': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredJobs = jobs.filter(job => {
    const matchesFilter = filter === 'all' || job.status?.toUpperCase() === filter.toUpperCase();
    const matchesSearch = !searchTerm ||
      job.propertyAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const statusCounts = {
    all: jobs.length,
    assigned: jobs.filter(j => j.status?.toUpperCase() === 'ASSIGNED').length,
    scheduled: jobs.filter(j => j.status?.toUpperCase() === 'SCHEDULED').length,
    in_progress: jobs.filter(j => j.status?.toUpperCase() === 'IN_PROGRESS').length,
    completed: jobs.filter(j => j.status?.toUpperCase() === 'COMPLETED').length,
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Wrench className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Jobs</h1>
          <p className="text-gray-600">View and manage your assigned maintenance jobs</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs by address, category, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              <option value="all">All Jobs ({statusCounts.all})</option>
              <option value="assigned">Assigned ({statusCounts.assigned})</option>
              <option value="scheduled">Scheduled ({statusCounts.scheduled})</option>
              <option value="in_progress">In Progress ({statusCounts.in_progress})</option>
              <option value="completed">Completed ({statusCounts.completed})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setFilter('assigned')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'assigned' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Assigned</p>
              <p className="text-2xl font-bold text-blue-600">{statusCounts.assigned}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-100" />
          </div>
        </button>

        <button
          onClick={() => setFilter('scheduled')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'scheduled' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Scheduled</p>
              <p className="text-2xl font-bold text-purple-600">{statusCounts.scheduled}</p>
            </div>
            <Calendar className="w-8 h-8 text-purple-100" />
          </div>
        </button>

        <button
          onClick={() => setFilter('in_progress')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'in_progress' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-yellow-600">{statusCounts.in_progress}</p>
            </div>
            <Wrench className="w-8 h-8 text-yellow-100" />
          </div>
        </button>

        <button
          onClick={() => setFilter('completed')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${filter === 'completed' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{statusCounts.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-100" />
          </div>
        </button>
      </div>

      {/* Jobs List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {filter === 'all' ? 'All Jobs' : `${filter.replace('_', ' ')} Jobs`}
          </h2>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-sm text-secondary hover:text-primary">
              Show all
            </button>
          )}
        </div>

        {filteredJobs.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'You have no jobs matching this filter.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredJobs.map(job => (
              <div
                key={job.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/contractor/job/${job.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                        {job.status?.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-sm font-medium ${getUrgencyColor(job.urgency)}`}>
                        {job.urgency} Priority
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-1">{job.category}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{job.description}</p>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.propertyAddress}
                      </span>
                      {job.scheduledDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.scheduledDate)}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

